import ITokenSettings from "@models/ITokenSettings";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import { FilterValue, IFilterPreset } from "@models/IFilterPresets";

/**
 * How one legacy `<screen>_filters` bag on ITokenSettings maps onto the new filterPresets structure.
 * The legacy bags store UI state names; presets store wire (API param) names.
 */
interface ILegacyFilterMigration {
    screenKey: ScreenOptionsEnum;
    /** legacy UI state name → wire name (keys not listed are already wire names) */
    keyMap: { [legacyKey: string]: string };
    /** legacy keys with no wire equivalent — dropped */
    drop: string[];
}

const ORDER_KEY_MAP = { order_state_id: "order_state", catalogGuid: "catalog" };

export const LEGACY_FILTER_MAP: { [legacyKey: string]: ILegacyFilterMigration } = {
    orders_filters: { screenKey: ScreenOptionsEnum.ORDERS, keyMap: ORDER_KEY_MAP, drop: [] },
    quotes_filters: { screenKey: ScreenOptionsEnum.QUOTES, keyMap: ORDER_KEY_MAP, drop: [] },
    shipments_filters: { screenKey: ScreenOptionsEnum.SHIPPING, keyMap: { shipmentType: "shipment_type" }, drop: ["shipment_status_id"] },
};

export const MIGRATED_PRESET_NAME = "My Filters";

/**
 * Translate a legacy filter bag into preset filters keyed by wire names.
 * Nil and empty-string values are omitted (they carried no filter).
 */
export function mapLegacyFilters(
    legacyFilters: { [key: string]: unknown },
    migration: Pick<ILegacyFilterMigration, "keyMap" | "drop">
): { [key: string]: FilterValue } {
    const filters: { [key: string]: FilterValue } = {};

    for (const [legacyKey, value] of Object.entries(legacyFilters)) {
        if (migration.drop.includes(legacyKey)) {
            continue;
        }

        if (value === null || value === undefined || value === "") {
            continue;
        }

        if (typeof value !== "string" && typeof value !== "boolean" && typeof value !== "number" && !Array.isArray(value)) {
            continue;
        }

        filters[migration.keyMap[legacyKey] || legacyKey] = value as FilterValue;
    }

    return filters;
}

/**
 * Lazy-migrate legacy filter properties to the new filterPresets structure.
 * Call this when loading token settings in FilterBar (v2) mode, before passing presets to FilterBar.
 *
 * Mutates the settings object in-place and returns true if a preset was created
 * (the caller should persist the full `filterPresets` object).
 *
 * The legacy keys are left untouched (neither deleted nor nulled): while the legacy/v2 toggle
 * exists, users who switch back to legacy keep their old saved filters. The null-delete step
 * ships in the release that removes the toggle.
 *
 * Idempotent: a screen is only migrated when `filterPresets[screenKey]` is absent or empty.
 */
export function migrateFilterPresets(settings: ITokenSettings): boolean {
    let migrated = false;

    for (const [legacyKey, migration] of Object.entries(LEGACY_FILTER_MAP)) {
        const legacyFilters = (settings as Record<string, unknown>)[legacyKey];

        if (!legacyFilters || typeof legacyFilters !== "object" || Array.isArray(legacyFilters)) {
            continue;
        }

        const existingPresets = settings.filterPresets?.[migration.screenKey];

        if (existingPresets && existingPresets.length > 0) {
            continue;
        }

        const filters = mapLegacyFilters(legacyFilters as { [key: string]: unknown }, migration);

        if (!Object.keys(filters).length) {
            continue;
        }

        const preset: IFilterPreset = {
            id: `migrated_${migration.screenKey}_${Date.now()}`,
            name: MIGRATED_PRESET_NAME,
            isDefault: true,
            filters,
        };

        settings.filterPresets = {
            ...(settings.filterPresets || {}),
            [migration.screenKey]: [preset],
        };

        migrated = true;
    }

    return migrated;
}
